import { useState } from "react";
import { b2bResetPassword } from "@/api/b2bAuth";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./B2bLoginPage.module.css";

interface ResetPasswordFormProps {
  email: string;
  token: string;
  onSuccess: () => void;
}

/** Umbraco's member password policy (mirrors the old frontend's validation). */
const MIN_PASSWORD_LENGTH = 10;

/** Bare "<status> <statusText>" errors from the Result layer carry no user-readable message. */
const HTTP_STATUS_ERROR_RE = /^\d{3}\s/;

export function ResetPasswordForm({
  email,
  token,
  onSuccess,
}: ResetPasswordFormProps) {
  const texts = useLocalizedTexts();
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(
        t(
          texts,
          "Tracker.MyAccountB2b.PasswordTooShort",
          `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
        )
      );
      return;
    }
    if (password !== repeatPassword) {
      setError(
        t(
          texts,
          "Tracker.MyAccountB2b.PasswordsDoNotMatch",
          "Passwords do not match"
        )
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const result = await b2bResetPassword(email, password, token);
    if (result.ok) {
      onSuccess();
      return;
    }

    setError(
      HTTP_STATUS_ERROR_RE.test(result.error)
        ? t(
            texts,
            "Tracker.Generic.Error.ResetPasswordFailed",
            "Could not reset the password. The link may have expired — please request a new one."
          )
        : result.error
    );
    setIsSubmitting(false);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.field}>
        <label className={styles.label} htmlFor="b2b-reset-email">
          {t(texts, "Tracker.Generic.Email", "Email")}
        </label>
        <input
          id="b2b-reset-email"
          className={styles.input}
          type="email"
          value={email}
          disabled
        />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="b2b-reset-password">
          {t(texts, "Tracker.Generic.Password", "Password")}
        </label>
        <input
          id="b2b-reset-password"
          className={styles.input}
          type="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="b2b-reset-repeat">
          {t(texts, "Tracker.Generic.RepeatPassword", "Repeat password")}
        </label>
        <input
          id="b2b-reset-repeat"
          className={styles.input}
          type="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          value={repeatPassword}
          onChange={(e) => setRepeatPassword(e.target.value)}
        />
      </div>
      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting}
      >
        {t(texts, "Tracker.Generic.ResetPassword", "Reset password")}
      </button>
    </form>
  );
}
